import React, { useState, useEffect, useMemo } from 'react';
import { Form, Row, Col, Modal, Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';
import TextInput from '../../form-generator/components/TextInput';
import DropDownInput from '../../form-generator/components/DropDownInput';
import CheckBoxGroup from '../../form-generator/components/CheckBoxGroup';
import { ADMIN_ROLE, PROCESSOR_ROLE, SALES_PERSON_ROLE } from '../../helpers/constants';
import { getCompanyEmail } from '../../helpers/datasheetConstants';
import { getUserFromLocalstrorage } from '../../helpers/global';
import useMunicipalityCounty from '../../hooks/useMunicipalityCounty';
import CurrencyInput from '../common/CurrencyInput';

const emptyTeamFields = {
  departmentRoles: '',
  supervisor: '',
  startDate: '',
  birthDate: '',
  compensationAmount: '',
  paymentFrequency: '',
  notes: '',
  hasFinancialIntelligenceAccess: false,
  isDefaultReportAnalyst: false
};

// Same background and height as the form's inputs; "&&" outranks the .select-organization overrides
const lienSearchCountiesSelectStyles = {
  control: base => ({
    ...base,
    '&&': {
      backgroundColor: 'rgba(246, 248, 250, 0.913) !important',
      minHeight: 'calc(1.5em + 0.75rem + 2px)'
    }
  })
};

const UserFormModal = ({
  show,
  user,
  roles,
  onHide,
  onSubmit,
  showProgress,
  usersFromOrganization,
  accurateTeamManagement = false,
  teamMembers = [],
  isTeamMembersLoading = false
}) => {
  const isEdit = !!user;

  const loggedInUser = getUserFromLocalstrorage();
  const isAdmin = loggedInUser?.role === ADMIN_ROLE;
  const isSuperuser = loggedInUser?.email?.trim()?.toLowerCase() === 'superuser@accurate.app';
  const { countyOptions } = useMunicipalityCounty();

  // Compute initial form data from user prop
  const initialFormData = useMemo(() => {
    const base = {
      name: user?.name || '',
      phone: user?.phone || '',
      email: getCompanyEmail(user) || '',
      role: user?.role || roles[0] || '',
      defaultProcessorFor: user?.defaultProcessorFor || [],
      lienSearchDefaultCounties: user?.lienSearchDefaultCounties || [],
      isEnabled: user?.isEnabled !== undefined ? user.isEnabled : true,
      hasFinancialIntelligenceAccess: !!user?.hasFinancialIntelligenceAccess,
      isDefaultReportAnalyst: !!user?.isDefaultReportAnalyst
    };
    if (!accurateTeamManagement) {
      return base;
    }
    return {
      ...base,
      departmentRoles: user?.departmentRoles || '',
      supervisor: user?.supervisor?._id || user?.supervisor || '',
      startDate: user?.startDate ? moment(user.startDate).format('YYYY-MM-DD') : '',
      birthDate: user?.birthDate ? moment(user.birthDate).format('YYYY-MM-DD') : '',
      compensationAmount:
        user?.compensationAmount !== undefined && user?.compensationAmount !== null
          ? String(user.compensationAmount)
          : '',
      paymentFrequency: user?.paymentFrequency || '',
      notes: user?.notes || ''
    };
  }, [user, roles, accurateTeamManagement]);

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  // Reset form data when modal opens or user changes
  useEffect(() => {
    if (show) {
      setFormData(initialFormData);
      setErrors({});
    } else {
      // Clear form data when modal closes
      setFormData({
        name: '',
        phone: '',
        email: '',
        role: '',
        defaultProcessorFor: [],
        lienSearchDefaultCounties: [],
        isEnabled: true,
        ...(accurateTeamManagement ? emptyTeamFields : {})
      });
      setErrors({});
    }
  }, [show, initialFormData, accurateTeamManagement]);

  const supervisorOptions = useMemo(
    () =>
      accurateTeamManagement
        ? (teamMembers || [])
            .filter(member => member?._id !== user?._id)
            .map(member => ({
              label: member.name,
              value: member._id
            }))
        : [],
    [accurateTeamManagement, teamMembers, user]
  );

  const selectedSupervisor = useMemo(
    () => supervisorOptions.find(option => option.value === formData.supervisor) || null,
    [formData.supervisor, supervisorOptions]
  );

  const selectedLienSearchCounties = useMemo(
    () => (formData.lienSearchDefaultCounties || []).map(county => ({ label: county, value: county })),
    [formData.lienSearchDefaultCounties]
  );

  const canEditFinancialIntelligenceAccess = useMemo(() => formData.role === ADMIN_ROLE && isAdmin && isSuperuser, [
    formData.role,
    isAdmin,
    isSuperuser
  ]);
  const canManageDefaultReportAnalyst = useMemo(
    () =>
      isAdmin &&
      formData.isEnabled !== false &&
      [ADMIN_ROLE, PROCESSOR_ROLE].includes(formData.role),
    [formData.isEnabled, formData.role, isAdmin]
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (!canEditFinancialIntelligenceAccess && formData.hasFinancialIntelligenceAccess) {
      setFormData(prev => ({
        ...prev,
        hasFinancialIntelligenceAccess: false
      }));
    }
  }, [canEditFinancialIntelligenceAccess, formData.hasFinancialIntelligenceAccess]);

  useEffect(() => {
    if (!canManageDefaultReportAnalyst && formData.isDefaultReportAnalyst) {
      setFormData(prev => ({ ...prev, isDefaultReportAnalyst: false }));
    }
  }, [canManageDefaultReportAnalyst, formData.isDefaultReportAnalyst]);

  const validateForm = () => {
    const newErrors = {};

    // Validate Name (always required)
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    // Validate Email
    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate start date
    if (accurateTeamManagement) {
      if (!formData.startDate || formData.startDate.trim() === '') {
        newErrors.startDate = 'Start date is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!onSubmit) {
      return;
    }

    if (accurateTeamManagement) {
      const payload = {
        ...formData,
        compensationAmount: formData.compensationAmount === '' ? null : Number(formData.compensationAmount)
      };
      if (!isAdmin) delete payload.isDefaultReportAnalyst;
      onSubmit(payload);
    } else {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        role: formData.role,
        defaultProcessorFor: formData.defaultProcessorFor,
        lienSearchDefaultCounties: formData.lienSearchDefaultCounties,
        isEnabled: formData.isEnabled,
        hasFinancialIntelligenceAccess: canEditFinancialIntelligenceAccess
          ? formData.hasFinancialIntelligenceAccess
          : false
      };
      if (isAdmin) {
        payload.isDefaultReportAnalyst = canManageDefaultReportAnalyst
          ? formData.isDefaultReportAnalyst
          : false;
      }
      onSubmit(payload);
    }
  };

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title>
          <h6 className="mb-0 xlargeFont font-weight-bold">{user ? `Update User` : `Invite User`}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto p-3">
        <Form id="user-form" onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <TextInput
                  id="name"
                  title="Name"
                  required
                  preValue={formData.name}
                  className={errors.name ? 'is-invalid' : ''}
                  onFormChange={e => handleInputChange('name', e.target.value)}
                />
                {errors.name && <div className="text-danger small mt-1">{errors.name}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <TextInput
                  id="phone"
                  title="Phone"
                  variant="tel"
                  preValue={formData.phone}
                  onFormChange={e => handleInputChange('phone', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <TextInput
                  id="email"
                  title="Email"
                  variant="email"
                  required
                  preValue={formData.email}
                  className={errors.email ? 'is-invalid' : ''}
                  onFormChange={e => handleInputChange('email', e.target.value)}
                />
                {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Role <span className="text-danger">*</span>
                </Form.Label>
                <DropDownInput
                  key={`role-${show}-${user?._id || 'new'}-${formData.role}`}
                  id="role"
                  title="Role"
                  options={roles || []}
                  required
                  preValue={formData.role}
                  className={errors.role ? 'is-invalid' : ''}
                  onFormChange={e => handleInputChange('role', e.target.value)}
                />
                {errors.role && <div className="text-danger small mt-1">{errors.role}</div>}
              </Form.Group>
            </Col>
            {isEdit && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Status <span className="text-danger">*</span>
                  </Form.Label>
                  <DropDownInput
                    key={`isEnabled-${show}-${user?._id || 'new'}-${formData.isEnabled}`}
                    id="isEnabled"
                    title="Status"
                    options={['Enable', 'Disable']}
                    optionValues={['true', 'false']}
                    required
                    preValue={String(formData.isEnabled)}
                    className={errors.isEnabled ? 'is-invalid' : ''}
                    onFormChange={e => handleInputChange('isEnabled', e.target.value === 'true')}
                  />
                  {errors.isEnabled && <div className="text-danger small mt-1">{errors.isEnabled}</div>}
                </Form.Group>
              </Col>
            )}
            
          </Row>

          {canEditFinancialIntelligenceAccess && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check className="d-flex align-items-center">
                    <Form.Check.Input
                      id="hasFinancialIntelligenceAccess"
                      type="checkbox"
                      checked={!!formData.hasFinancialIntelligenceAccess}
                      style={{ width: '1.35rem', height: '1.35rem', marginTop: 0 }}
                      onChange={e => handleInputChange('hasFinancialIntelligenceAccess', e.target.checked)}
                    />
                    <Form.Check.Label
                      htmlFor="hasFinancialIntelligenceAccess"
                      style={{ marginLeft: '0.5rem', marginBottom: 0 }}
                    >
                      Show Financial Intelligence Dashboard
                    </Form.Check.Label>
                  </Form.Check>
                </Form.Group>
              </Col>
            </Row>
          )}

          {formData.role !== SALES_PERSON_ROLE && (accurateTeamManagement || (isAdmin && !usersFromOrganization)) && (
            <>
              <Row className="mt-2">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Processor For</Form.Label>
                    <CheckBoxGroup
                      key={`defaultProcessorFor-${show}-${user?._id || 'new'}-${JSON.stringify(
                        formData.defaultProcessorFor
                      )}`}
                      id="defaultProcessorFor"
                      groupName="defaultProcessorFor"
                      title="Default Processor For"
                      options={['Lien Search', 'Estoppel', 'Survey']}
                      optionValues={['Lien Search', 'Estoppel', 'Survey']}
                      boxWidth={4}
                      preValue={formData.defaultProcessorFor}
                      onChangeFunction={e => {
                        // CheckBoxGroup component updates DOM directly, so we read from DOM
                        const checkboxes = document.getElementsByName('defaultProcessorFor');
                        const selected = Array.from(checkboxes)
                          .filter(cb => cb.checked)
                          .map(cb => cb.value);
                        handleInputChange('defaultProcessorFor', selected);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Lien Processor For Counties</Form.Label>
                    <Select
                      isMulti
                      placeholder="Select counties"
                      isDisabled={showProgress}
                      options={countyOptions}
                      value={selectedLienSearchCounties}
                      onChange={options =>
                        handleInputChange(
                          'lienSearchDefaultCounties',
                          (options || []).map(option => option.value)
                        )
                      }
                      closeMenuOnSelect={false}
                      className="basic-multi-select select-organization"
                      classNamePrefix="select"
                      styles={lienSearchCountiesSelectStyles}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {canManageDefaultReportAnalyst && (
                <Col md={6} className="px-0 pt-2">
                  <div className="d-flex align-items-center mb-3" style={{ gap: '0.5rem' }}>
                    <Form.Label htmlFor="isDefaultReportAnalyst" className="mb-0">
                      Default Report Analyst
                    </Form.Label>
                    <Form.Check
                      id="isDefaultReportAnalyst"
                      type="switch"
                      checked={!!formData.isDefaultReportAnalyst}
                      className="mb-0 flex-shrink-0 switch-info switch-lg shadow-none"
                      onChange={e => handleInputChange('isDefaultReportAnalyst', e.target.checked)}
                    />
                  </div>
                </Col>
              )}
            </>
          )}

          {accurateTeamManagement && (
            <>
              <p className="font-weight-bold largeFont py-2 my-2 border-top">Contractor Details</p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Role/Department</Form.Label>
                    <TextInput
                      id="departmentRoles"
                      hint="e.g., Data Processor"
                      placeholder="e.g., Data Processor"
                      title="Role/Department"
                      preValue={formData.departmentRoles}
                      onFormChange={e => handleInputChange('departmentRoles', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Supervisor</Form.Label>
                    <Select
                      placeholder={isTeamMembersLoading ? 'Loading users...' : 'Select supervisor'}
                      isDisabled={isTeamMembersLoading || showProgress}
                      options={supervisorOptions}
                      value={selectedSupervisor}
                      onChange={option => handleInputChange('supervisor', option?.value || '')}
                      isClearable
                      isSearchable
                      className="basic-single select-organization"
                      classNamePrefix="select"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Start Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.startDate}
                      onChange={e => handleInputChange('startDate', e.target.value)}
                      className={errors.startDate ? 'is-invalid' : ''}
                    />
                    {errors.startDate && <div className="text-danger small mt-1">{errors.startDate}</div>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Birth Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.birthDate}
                      onChange={e => handleInputChange('birthDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Compensation</Form.Label>
                    <CurrencyInput
                      value={formData.compensationAmount}
                      onChange={value => handleInputChange('compensationAmount', value)}
                      height="36px"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Frequency</Form.Label>
                    <DropDownInput
                      key={`paymentFrequency-${show}-${user?._id || 'new'}-${formData.paymentFrequency}`}
                      id="paymentFrequency"
                      title="Payment Frequency"
                      options={['Monthly', 'Bi-Weekly', 'Twice Monthly']}
                      preValue={formData.paymentFrequency}
                      onFormChange={e => handleInputChange('paymentFrequency', e.target.value)}
                      required={false}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.notes}
                      onChange={e => handleInputChange('notes', e.target.value)}
                      placeholder="Add notes"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={showProgress}>
          Cancel
        </Button>
        <Button
          variant="info"
          className="d-inline-flex align-items-center justify-content-center"
          onClick={handleSubmit}
          disabled={showProgress}
        >
          {showProgress && <Spinner size="sm" animation="border" className="mr-1" />}
          {user ? 'Update' : 'Invite'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserFormModal;
